<template>
  <Teleport to="body">
    <div v-if="show" class="modal-overlay" @click.self="$emit('close')">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ $t('rescan.title') }}</h3>
          <button @click="$emit('close')" class="btn btn-icon">
            <font-awesome-icon icon="fa-solid fa-xmark" />
          </button>
        </div>

        <div class="modal-body">
          <p class="modal-description">
            {{ $t('rescan.description') }}
          </p>

        <!-- Error Message -->
        <div v-if="error" class="error-banner">
          <font-awesome-icon icon="fa-solid fa-circle-exclamation" />
          {{ error }}
        </div>

        <div class="modal-options">
          <!-- Normal Scan Option -->
          <button
            @click="$emit('rescan', false)"
            class="modal-option-btn normal-scan"
            :disabled="loading"
          >
            <div class="option-icon">
              <font-awesome-icon icon="fa-solid fa-file-circle-check" style="font-size: 48px;" />
            </div>
            <h4>{{ $t('rescan.normalScan') }}</h4>
            <p>{{ $t('rescan.normalScanDesc') }}</p>
          </button>

          <!-- Hard Scan Option -->
          <button
            @click="$emit('rescan', true)"
            class="modal-option-btn hard-scan"
            :disabled="loading || !canUseHardScan"
            :class="{ 'disabled': !canUseHardScan }"
          >
            <div class="option-icon">
              <font-awesome-icon icon="fa-solid fa-file-waveform" style="font-size: 48px;" />
            </div>
            <h4>
              {{ $t('rescan.hardScan') }}
              <span v-if="canUseHardScan" class="premium-badge">PREMIUM</span>
            </h4>
            <p>{{ $t('rescan.hardScanDesc', { remaining: hardScansRemaining, limit: hardScanLimit }) }}</p>
            <p v-if="!canUseHardScan" class="no-scans-warning">{{ $t('rescan.noHardScans') }}</p>
          </button>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-overlay">
          <div class="loading-spinner"></div>
          <p>{{ $t('rescan.rescanning') }}</p>
        </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

defineProps({
  show: {
    type: Boolean,
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  },
  error: {
    type: String,
    default: null
  },
  canUseHardScan: {
    type: Boolean,
    default: false
  },
  hardScansRemaining: {
    type: Number,
    default: 0
  },
  hardScanLimit: {
    type: Number,
    default: 0
  }
});

defineEmits(['close', 'rescan']);
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-content {
  background: white;
  border-radius: 16px;
  padding: 0;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;
  position: relative;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  padding: 24px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
}

.modal-body {
  padding: 24px;
}

.modal-description {
  margin: 0 0 24px 0;
  color: var(--text-secondary);
  font-size: 15px;
  text-align: center;
}

.error-banner {
  background: #fee2e2;
  border: 1px solid #fca5a5;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #dc2626;
  font-size: 14px;
}

.error-banner svg {
  flex-shrink: 0;
}

.modal-options {
  display: grid;
  gap: 12px;
}

.modal-option-btn {
  width: 100%;
  padding: 20px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: white;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.modal-option-btn:not(:disabled):hover {
  border-color: var(--primary);
  background: var(--primary-light);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.15);
}

.modal-option-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.modal-option-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.option-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  color: var(--primary);
}

.modal-option-btn h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
}

.premium-badge {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 4px;
  letter-spacing: 0.5px;
}

.modal-option-btn p {
  margin: 0;
  font-size: 14px;
  color: var(--text-secondary);
}

.no-scans-warning {
  color: #dc2626 !important;
  font-weight: 500;
  font-size: 13px !important;
}

.modal-option-btn.normal-scan:not(:disabled):hover {
  border-color: #10b981;
  background: #ecfdf5;
}

.modal-option-btn.normal-scan:not(:disabled):hover h4 {
  color: #10b981;
}

.modal-option-btn.normal-scan:not(:disabled):hover .option-icon {
  color: #10b981;
}

.modal-option-btn.hard-scan:not(:disabled):hover {
  border-color: #f59e0b;
  background: #fffbeb;
}

.modal-option-btn.hard-scan:not(:disabled):hover h4 {
  color: #f59e0b;
}

.modal-option-btn.hard-scan:not(:disabled):hover .option-icon {
  color: #f59e0b;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e2e8f0;
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-overlay p {
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  color: var(--text-primary);
}

@media (max-width: 640px) {
  .modal-content {
    width: 95%;
    margin: 20px;
  }

  .modal-header {
    padding: 20px;
  }

  .modal-body {
    padding: 20px;
  }

  .modal-option-btn {
    padding: 16px;
  }
}
</style>
